import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { aUnChapitre } from "@/lib/mon-travail";

/**
 * Mode d'affichage de l'espace paroisse (décision PO du 10/10/2026) :
 * « travail » (les cinq actions du chapitre du manuel en tête du menu) ou
 * « standard » (le menu de la page 6 du manuel). Mode travail par défaut.
 *
 * Le choix est gardé sur le compte (BFF `/api/micro/preferences/affichage`,
 * réglages d'auth-service) et recopié dans le navigateur pour s'afficher dès
 * le premier rendu. Un rôle sans chapitre dans le manuel reste en standard.
 */
export type ModeAffichage = "travail" | "standard";

const CLE_LOCALE = "paroisses-mode-affichage";
const CLE_QUERY = ["preferences", "affichage"] as const;

function lireLocal(): ModeAffichage | null {
  try {
    const v = localStorage.getItem(CLE_LOCALE);
    return v === "travail" || v === "standard" ? v : null;
  } catch {
    return null;
  }
}

function ecrireLocal(mode: ModeAffichage) {
  try { localStorage.setItem(CLE_LOCALE, mode); } catch {}
}

export function useModeAffichage(role: string) {
  const qc = useQueryClient();
  const possible = aUnChapitre(role);

  const { data } = useQuery({
    queryKey: CLE_QUERY,
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/micro/preferences/affichage");
      const corps = (await res.json()) as { mode?: ModeAffichage | null };
      if (corps?.mode) ecrireLocal(corps.mode);
      return corps?.mode ?? null;
    },
    enabled: possible,
    retry: false,
  });

  const changer = useMutation({
    mutationFn: async (mode: ModeAffichage) => {
      await apiRequest("PUT", "/api/micro/preferences/affichage", { mode });
      return mode;
    },
    onMutate: (mode) => {
      ecrireLocal(mode);
      qc.setQueryData(CLE_QUERY, mode);
    },
  });

  const mode: ModeAffichage = !possible ? "standard" : (data ?? lireLocal() ?? "travail");
  return { mode, possible, changer: (m: ModeAffichage) => changer.mutate(m), enregistrementEchoue: changer.isError };
}
